import PropTypes from 'prop-types';

/**
 * @namespace Utils
 * @private
 * @memberof Mojito.Presentation.Components.Scoreboard
 */

/**
 * Returns  the name of a specific participant given their index.
 *
 * @param {Array} participants - The event participants.
 * @param {number} index - The index of the participant whose name is to be retrieved.
 *
 * @returns {string} Name of the participant corresponding to the provided index.
 * @function Mojito.Presentation.Components.Scoreboard.Utils#getParticipantName
 */
export function getParticipantName(participants, index) {
    return participants && index < participants.length && participants[index].name;
}

const PARTICIPANT_POSITION = {
    HOME: 'HOME',
    AWAY: 'AWAY',
};

/**
 * Sort the participants of an event according to which should be displayed first, home team or away team.
 *
 * @param {Array|undefined} participants - List to be sorted.
 * @param {boolean} homeAway - True if home team should be first in list.
 *
 * @returns {Array} List of sorted participants.
 *
 * @function Mojito.Presentation.Components.Scoreboard.Utils#sortParticipants
 */

export const sortParticipants = (participants, homeAway) => {
    const { AWAY, HOME } = PARTICIPANT_POSITION;
    return participants?.toSorted((p1, p2) => {
        if (p1.position === HOME && p2.position === AWAY) {
            return homeAway ? -1 : 1;
        }
        if (p1.position === AWAY && p2.position === HOME) {
            return homeAway ? 1 : -1;
        }
        return 0;
    });
};

/**
 * Returns participants position in lower case.
 *
 * @param {object} event - The event to display.
 *
 * @returns {Array} An array containing the positions of participants in lowercase.
 * @function Mojito.Presentation.Components.Scoreboard.Utils#getParticipantsPosition
 */
export function getParticipantsPosition(event) {
    const participants = sortParticipants(event?.participants, event?.homeAway);

    return [participants[0].position.toLowerCase(), participants[1].position.toLowerCase()];
}

/**
 * Returns the total score of a specific participant from the event data.
 *
 * @param {object} event - The event to display.
 * @param {string} position - Participant position 'HOME' or 'AWAY'.
 * @param {string} totalScoresProp - The property name within `event.details` which contains the scores.
 *
 * @returns {string} The score of the participant at the specified index.
 * @function Mojito.Presentation.Components.Scoreboard.Utils#getScore
 */
export function getScore(event, position, totalScoresProp) {
    return event.details[totalScoresProp][position.toLowerCase()];
}

/**
 * Returns the participant who is currently serving in the event.
 *
 * @param {object} event - The event to display.
 * @param {string} position - Participant position 'HOME' or 'AWAY'.
 *
 * @returns {boolean} True is participant is sever, false otherwise.
 * @function Mojito.Presentation.Components.Scoreboard.Utils#getServers
 */
export function isServer(event, position) {
    const { server } = event.details;
    return server ? position.toLowerCase() === server.toLowerCase() : false;
}

/**
 * Retrieves detailed participant data for the scoreboard.
 *
 * @param {object} event - The event to be displayed.
 * @param {string} totalScoresProp - The property name from `event.details`.
 *
 * @returns {Array} Participant info items. Each item has props (name, score, server, position).
 * @function Mojito.Presentation.Components.Scoreboard.Utils#getDetailedParticipantsData
 */
export function getDetailedParticipantsData(event, totalScoresProp) {
    const participants = sortParticipants(event?.participants, event?.homeAway);

    return participants.map(participant => {
        const { position } = participant;
        return {
            name: participant.name,
            score: getScore(event, position, totalScoresProp),
            server: isServer(event, position),
            position,
        };
    });
}

/**
 * Returns segment's name translation with the corresponding number by sport.
 *
 * @param {string} number - Segment's number.
 * @param {string} sportId - The sport id.
 * @param {string} defaultProp - Default property in translations when the given sport id is not supported in the translations.
 * @param {object} l10n - All available translations.
 *
 * @returns {string} The segment's name translation with the corresponding number based on sports id.
 * @function Mojito.Presentation.Components.Scoreboard.Utils#getSegmentName
 */
export function getSegmentName(number, sportId, defaultProp, l10n) {
    const scoreboardHeader = l10n.SCOREBOARD.HEADER;
    const prefix =
        scoreboardHeader.SEGMENT_PREFIX[sportId] || scoreboardHeader.SEGMENT_PREFIX[defaultProp];
    return `${prefix}${number}`;
}

/**
 * Returns overtime's name translation with the corresponding number.
 *
 * @param {string} number - The overtime number. If undefined, only the prefix will be shown.
 * @param {string} defaultProp - The default property in translations.
 * @param {object} l10n - All available translations.
 *
 * @returns {string} The overtime's name translation with its corresponding number if provided.
 * @function Mojito.Presentation.Components.Scoreboard.Utils#getOvertimeName
 */
export function getOvertimeName(number, defaultProp, l10n) {
    const prefix = l10n.SCOREBOARD.HEADER.SEGMENT_PREFIX[defaultProp];
    return `${prefix}${number || ''}`;
}

/**
 * Returns total score label translation by sport.
 *
 * @param {string} sportId - The sport id.
 * @param {object} systemSettings - Settings including applicationMode, language information, etc.
 * @param {string} l10n - All available translations.
 *
 * @returns {string} The total score label translation for the given sport.
 * @function Mojito.Presentation.Components.Scoreboard.Utils#getTotalScoreString
 */
export function getTotalScoreString(sportId, systemSettings, l10n) {
    const totalScoreStrings = l10n.SCOREBOARD.HEADER.TOTAL_SCORE;
    const applicationMode = systemSettings.applicationMode.toUpperCase();

    return totalScoreStrings[sportId]
        ? totalScoreStrings[sportId][applicationMode]
        : totalScoreStrings.DEFAULT[applicationMode];
}

/**
 * Returns the wicket count segment from detailed innings scores for a participant side.
 *
 * @param {object} event - The event to display.
 * @param {string} position - Participant position `HOME` or `AWAY`.
 *
 * @returns {string} The wickets substring after `/` for the given side from `detailedScores` entries, whenever it parses as a positive integer (later segments overwrite earlier).
 * @function Mojito.Presentation.Components.Scoreboard.Utils#getWicket
 */
export function getWicket(event, position) {
    let result = '';
    event.details.detailedScores.forEach(score => {
        const wicket = score[position.toLowerCase()].split('/')[1] || '';
        result = parseInt(wicket, 10) > 0 ? wicket : result;
    });
    return result;
}

export function getPropTypes() {
    return {
        event: PropTypes.object.isRequired,
        inplayTime: PropTypes.number,
        hasClockDetails: PropTypes.bool,
        showEventIndicators: PropTypes.bool,
    };
}
